import { Injectable, OnDestroy } from "@angular/core";

import { AbstractControl, FormGroup } from "@angular/forms";
import {
  ButtonConfigurationFilters,
  UrlSubstringWhitelist
} from "@onsip/common/interfaces/organization-configuration";
import { Verb } from "@onsip/common/interfaces/verb";
import { ListPickerItem } from "@onsip/web/features/shared/components/pickers/listPicker/list-picker-item";

import { Subject, Observable } from "rxjs";
import { InvalidTopicFormError } from "../invalid-topic-form-error";
import { ConfigurationPatch } from "./configuration.service";

@Injectable({ providedIn: "root" })
export class TopicFormService implements OnDestroy {
  /** Internal overarching FormGroup */
  private _form: FormGroup = new FormGroup({});

  /** Internal observable tracking submit state */
  private _submitted = new Subject<void>();

  constructor() {
    this.reset();
  }

  ngOnDestroy() {
    this._submitted.next();
    this._submitted.complete();
  }

  reset(): void {
    this._form = new FormGroup({});
  }

  /** Send submit event to internal observable
   *
   * HACK: The submit observable is essentially a hack so nested components can be alerted when the form gets submitted.
   * This is necessary after we moved the sayso folder components to changeDetection.onPush.
   * With the onPush changes, the onsipsubmitdirective can no longer trigger error states in nested components
   * so we need to pass down the submit event directly to the nested components
   */
  sendSubmitEvent(): void {
    this._submitted.next();
  }

  /** Retrieve internal observable to be used in sub component */
  getSubmitObs(): Observable<void> {
    return this._submitted.asObservable();
  }

  /** Retrieves the internal FormGroup, it's marked as Readonly but there's no enforcing that */
  getForm(): Readonly<FormGroup> {
    return this._form;
  }

  /** Retrieves an individual control of the internal FormGroup, it's marked as Readonly but there's no enforcing that */
  getControl(name: string): Readonly<AbstractControl> | undefined {
    return this._form.get(name) || undefined;
  }

  addControl(name: string, control: AbstractControl): void {
    this._form.addControl(name, control);
  }

  removeControl(name: string): void {
    this._form.removeControl(name);
  }

  getConfigurationPatch(): ConfigurationPatch {
    if (this._form.valid) {
      const { targetDisplay, video } = this._form.value;
      if (typeof targetDisplay !== "undefined" && typeof video !== "undefined") {
        return { targetDisplay, video };
      }
    }
    throw new InvalidTopicFormError("");
  }

  getFilters(): ButtonConfigurationFilters {
    if (this._form.valid) {
      // getRawValue includes values from disabled form controls.
      // https://stackoverflow.com/questions/40148102/angular-2-disabled-controls-do-not-get-included-in-the-form-value
      const { whitelistCountries, blacklistCountries, pages, holidays, bhr } =
        this._form.getRawValue();
      // TODO after holiday splits out of bhr we'll refactor this
      if (typeof bhr !== "undefined") bhr.holidays = holidays;
      const filters: ButtonConfigurationFilters = {};
      filters.bhr = typeof bhr !== "undefined" ? [bhr] : undefined;
      filters.countryBlacklist =
        typeof blacklistCountries !== "undefined"
          ? [this.parseCountries(blacklistCountries)]
          : undefined;
      filters.countryWhitelist =
        typeof whitelistCountries !== "undefined"
          ? [this.parseCountries(whitelistCountries)]
          : undefined;
      filters.urlSubstringWhitelist =
        typeof pages !== "undefined" ? [this.parsePages()] : undefined;
      return filters;
    } else {
      throw new InvalidTopicFormError("");
    }
  }

  getReps(): Array<number> {
    if (this._form.valid) {
      const { reps }: { reps: Array<string> } = this._form.value;
      if (typeof reps !== "undefined") {
        return reps.map(repId => parseInt(repId)); // repId === userId
      } else {
        console.error(reps);
        throw new Error("TopicFormService.getReps: invalid reps");
      }
    } else {
      throw new InvalidTopicFormError("");
    }
  }

  private parseCountries(countries: Array<ListPickerItem>): Array<string> {
    if (this._form.valid) {
      return countries.map((country: ListPickerItem) => country.id);
    } else return [];
  }

  private parsePages(): UrlSubstringWhitelist {
    if (this._form.valid) {
      const pages: UrlSubstringWhitelist = [];
      const form = this._form.getRawValue();
      form.pages.rules.forEach((rule: { verb: Verb; url: string }) => {
        pages.push([rule.verb, rule.url]);
      });
      form.pages.exceptions.forEach((exception: { verb: Verb; url: string }) => {
        pages.push([exception.verb, exception.url]);
      });
      return pages;
    } else return [];
  }
}
